import React from "react";
import PropTypes from "prop-types";
import Card from "shared/Card";
import { formatCents } from "shared/@utils/money";
import MoneyInput from "shared/MoneyInput";
import CollapseToggle from "../CollapseToggle";
import BundleProductPicker from "./BundleProductPicker";
import styles from "./BundlePricingCard.module.css";

function sumCents(items, field) {
  return items.reduce((sum, item) => {
    const v = item[field];
    return sum + (typeof v === "number" ? v : 0);
  }, 0);
}

export function BundlePricingCard({
  pricing,
  bundledProducts,
  bundlePricingMode,
  onPricingChange,
  onBundleModeChange,
  onBundledProductsChange,
  currency,
}) {
  const safe = pricing || {};
  const items = bundledProducts || [];
  const mode = bundlePricingMode || "evenly";
  const [collapsed, setCollapsed] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);

  const emit = (patch) => {
    onPricingChange?.({
      lastPurchaseCents: safe.lastPurchaseCents ?? null,
      premierMemberCents: safe.premierMemberCents ?? null,
      retailCents: safe.retailCents ?? null,
      bundledCents: safe.bundledCents ?? null,
      ...patch,
    });
  };

  const sumLastPurchase = sumCents(items, "lastPurchaseCents");
  const sumPremierMember = sumCents(items, "premierMemberCents");

  // A bundle can contain the same product more than once with a different
  // variation/size (see backend Product.bundledProducts), so productId alone
  // isn't a row identity — operate on row indexes instead.
  const removeProduct = (index) => {
    onBundledProductsChange?.(items.filter((_, i) => i !== index));
  };

  const updateItemPrice = (index, cents) => {
    onBundledProductsChange?.(
      items.map((p, i) => (i === index ? { ...p, bundledItemCents: cents } : p))
    );
  };

  // A bundle's contents were previously only removable — there was no way to
  // put a product *into* one from the editor.
  const addProducts = (chosen) => {
    setPickerOpen(false);
    if (!chosen || !chosen.length) return;
    onBundledProductsChange?.([...items, ...chosen]);
  };

  return (
    <Card
      className={`${styles.card}${collapsed ? ` ${styles.cardCollapsed}` : ""}`}
    >
      <div className={styles.header}>
        <span className={styles.title}>Bundle Pricing</span>
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => setCollapsed((v) => !v)}
        />
      </div>
      {!collapsed && (
        <>
          <div className={styles.divider} />

          <div className={styles.row}>
            <span className={styles.label}>Last Purchase Price (Sum):</span>
            <span className={styles.readonly}>
              {formatCents(sumLastPurchase, currency)}
            </span>
          </div>

          <div className={styles.row}>
            <label className={styles.label} htmlFor="bp-pm">
              Premier Member Price
            </label>
            <div className={styles.valueGroup}>
              <span className={styles.dollar}>$</span>
              <MoneyInput
                id="bp-pm"
                value={safe.premierMemberCents}
                onChange={(cents) => emit({ premierMemberCents: cents })}
                placeholder="00.00"
              />
            </div>
          </div>

          <div className={styles.row}>
            <label className={styles.label} htmlFor="bp-retail">
              Retail Price
            </label>
            <div className={styles.valueGroup}>
              <span className={styles.dollar}>$</span>
              <MoneyInput
                id="bp-retail"
                value={safe.retailCents}
                onChange={(cents) => emit({ retailCents: cents })}
                placeholder="00.00"
              />
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>Reduce Item Pricing</span>
          </div>

          <div className={styles.toggleRow}>
            <button
              type="button"
              className={`${styles.toggleBtn} ${
                mode === "evenly" ? styles.toggleActive : ""
              }`}
              onClick={() => onBundleModeChange?.("evenly")}
              title="Split the bundle's reduced pricing evenly across all bundled items."
            >
              Evenly
            </button>
            <button
              type="button"
              className={`${styles.toggleBtn} ${
                mode === "custom" ? styles.toggleActive : ""
              }`}
              onClick={() => onBundleModeChange?.("custom")}
              title="Set a custom reduced price for each bundled item individually."
            >
              Custom
            </button>
          </div>

          <div className={styles.row}>
            <span className={styles.label}>Regular PM Price (Sum):</span>
            <span className={styles.readonly}>
              {formatCents(sumPremierMember, currency)}
            </span>
          </div>

          <div className={styles.row}>
            <label className={styles.label} htmlFor="bp-bundled">
              Bundled Price:
            </label>
            <div className={styles.valueGroup}>
              <span className={styles.dollar}>$</span>
              <MoneyInput
                id="bp-bundled"
                value={safe.bundledCents}
                onChange={(cents) => emit({ bundledCents: cents })}
                placeholder="00.00"
              />
            </div>
          </div>

          <div className={styles.divider} />
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>Bundled Products</span>
            <button
              type="button"
              className={styles.selectProductsBtn}
              onClick={() => setPickerOpen(true)}
              title="Choose which products this bundle contains."
            >
              Select Products
            </button>
          </div>
          {items.length === 0 && (
            <p className={styles.emptyProducts}>
              No products in this bundle yet.
            </p>
          )}
          {items.length > 0 && (
            <>
              {items.map((item, index) => (
                <div
                  key={`${item.productId}|${item.variationLabel || ""}|${
                    item.size || ""
                  }|${index}`}
                  className={styles.productRow}
                >
                  <div className={styles.productInfo}>
                    {item.thumbnailUrl && (
                      <img
                        className={styles.productThumb}
                        src={item.thumbnailUrl.url || item.thumbnailUrl}
                        alt={item.title || item.sku}
                      />
                    )}
                    <div className={styles.productMeta}>
                      <span className={styles.productName}>
                        {item.title || item.sku}
                      </span>
                      <span className={styles.productDetail}>
                        {item.sku && <span>SKU: {item.sku}</span>}
                        {item.variationLabel && (
                          <span> | Variation: {item.variationLabel}</span>
                        )}
                        {item.size && <span> | Size: {item.size}</span>}
                      </span>
                      <span className={styles.productDetail}>
                        R: {formatCents(item.retailCents, currency)} / PM:{" "}
                        {formatCents(item.premierMemberCents, currency)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.productActions}>
                    {mode === "custom" && (
                      <div className={styles.itemPriceGroup}>
                        <span className={styles.dollarSmall}>$</span>
                        <MoneyInput
                          value={item.bundledItemCents}
                          onChange={(cents) => updateItemPrice(index, cents)}
                          placeholder="00.00"
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={() => removeProduct(index)}
                      title="Remove this product from the bundle."
                    >
                      &#x2715;
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
          <BundleProductPicker
            isOpen={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onAdd={addProducts}
          />
        </>
      )}
    </Card>
  );
}

BundlePricingCard.propTypes = {
  pricing: PropTypes.shape({
    lastPurchaseCents: PropTypes.number,
    premierMemberCents: PropTypes.number,
    retailCents: PropTypes.number,
    bundledCents: PropTypes.number,
  }),
  bundledProducts: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string.isRequired,
      sku: PropTypes.string,
      title: PropTypes.string,
      retailCents: PropTypes.number,
      premierMemberCents: PropTypes.number,
      lastPurchaseCents: PropTypes.number,
      variationLabel: PropTypes.string,
      size: PropTypes.string,
      thumbnailUrl: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ url: PropTypes.string, id: PropTypes.string }),
      ]),
      bundledItemCents: PropTypes.number,
    })
  ),
  bundlePricingMode: PropTypes.oneOf(["evenly", "custom"]),
  onPricingChange: PropTypes.func,
  onBundleModeChange: PropTypes.func,
  onBundledProductsChange: PropTypes.func,
  currency: PropTypes.string,
};

BundlePricingCard.defaultProps = {
  pricing: null,
  bundledProducts: [],
  bundlePricingMode: "evenly",
  onPricingChange: undefined,
  onBundleModeChange: undefined,
  onBundledProductsChange: undefined,
  currency: "USD",
};

export default BundlePricingCard;
